// @ts-check
(() => {
  const setUpHomepageVideo = () => {
    const VIDEO_WRAPPER_SELECTOR = ".homepage-header__video";
    const videoWrapper = document.querySelector(VIDEO_WRAPPER_SELECTOR);
    if (!(videoWrapper instanceof HTMLElement)) {
      return;
    }

    const { videoId: videoId } = videoWrapper.dataset;
    const { videoProvider: provider } = videoWrapper.dataset;

    if (!videoId) {
      console.error(`The \`${VIDEO_WRAPPER_SELECTOR}\` element must have a \`data-video-id\` but doesn't.`);
      return;
    }

    if (!provider) {
      console.error(`The \`${VIDEO_WRAPPER_SELECTOR}\` element must have a \`data-video-provider\` but doesn't.`);
      return;
    }

    const setUpVideo = () => {

      const VIDEO_BUTTON_SELECTOR = ".homepage-header__vimeo-video-button";
      const HERO_OVERLAY_SELECTOR = ".homepage-header__video-image-overlay";

      const videoButton = document.querySelector(VIDEO_BUTTON_SELECTOR);
      if (!(videoButton instanceof HTMLButtonElement)) {
        console.error(`No \`${VIDEO_BUTTON_SELECTOR}\` element found.`);
        return;
      }

      const heroOverlay = document.querySelector(HERO_OVERLAY_SELECTOR);
      if (!(heroOverlay instanceof HTMLElement)) {
        console.error(`No \`${HERO_OVERLAY_SELECTOR}\` element found.`);
        return;
      }

      const PREFERS_PAUSED_KEY = "prefersPaused";

      /** @param {boolean} newValue */
      const setPrefersPaused = (newValue) => {
        window.localStorage.setItem(PREFERS_PAUSED_KEY, String(newValue));
      };

      const prefersPaused = () => window.localStorage.getItem(PREFERS_PAUSED_KEY) === "true";

      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

      const autoplay = !prefersPaused() && !prefersReducedMotion.matches;

      if (provider == "vimeo") {
        /** @typedef {typeof Vimeo} VimeoPlayer */

        const { Vimeo } = /** @type {typeof window & { Vimeo?: { Player: VimeoPlayer } }} */ (window);
        if (!Vimeo) {
          console.error(`Vimeo player SDK should be loaded but it isn't.`);
          return;
        }

        const player = new Vimeo.Player(videoWrapper, {
          id: Number(videoId),
          controls: false,
          keyboard: false,
          loop: true,
          muted: true,
          title: false,
          autoplay,
        });

        let isFirstPlay = true;

        // As soon as `iframe` is created: disallow tabbing into it, and reveal the toggle-button.
        player
          .ready()
          .then(() => {
            const iframe = videoWrapper.querySelector("iframe");
            if (!iframe) {
              console.error(`No iframe found inside \`${VIDEO_WRAPPER_SELECTOR}\` element.`);
              return;
            }
            iframe.tabIndex = -1;
            videoButton.dataset.videoReady = "true";
          })
          .catch(console.error);

        player.on("play", () => {
          videoButton.setAttribute("aria-pressed", "true");
          if (isFirstPlay) {
            isFirstPlay = false;
            delete videoButton.dataset.videoLoading;
            heroOverlay.dataset.videoStarted = "true";
          }
        });

        player.on("pause", () => {
          videoButton.setAttribute("aria-pressed", "false");
        });

        videoButton.addEventListener("click", () => {
          player
            .getPaused()
            .then((paused) => {
              if (paused) {
                if (isFirstPlay) {
                  videoButton.dataset.videoLoading = "true";
                }
                player.play().catch(console.error);
                setPrefersPaused(false);
              } else {
                player.pause().catch(console.error);
                setPrefersPaused(true);
              }
            })
            .catch(console.error);
        });
      } // Vimeo

      if (provider == "youtube") {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

        const player = new YT.Player(videoWrapper, {
          videoId: videoId,
          playerVars: {
            playsinline: 1,
            controls: 0,
            autoplay: 1,
            mute: 1,
            playlist: videoId,
            loop: 1,
            rel: 0,
          },
          events: {
            onReady: onPlayerReady,
            onStateChange: onPlayerStateChange,
          },
        });

        let paused = false;

        function onPlayerReady() {
          if (prefersReducedMotion.matches || window.localStorage.getItem(PREFERS_PAUSED_KEY) === "true") {
            // If user's OS is set to reduce motion
            // or if prefersPaused local storage is set to true
            // do not autoplay the video
            videoButton.setAttribute("aria-pressed", "false");
            pauseVideo();
          } else {
            // Start the video when ready
            videoButton.setAttribute("aria-pressed", "true");
            playVideo();
          }
        }

        function onPlayerStateChange(event) {
          // Test for state changes
        }

        function playVideo() {
          player.playVideo();
          videoButton.setAttribute("aria-pressed", "true");
          paused = false;
          heroOverlay.dataset.videoStarted = "true";
          setPrefersPaused(false);
        }
        function pauseVideo() {
          player.pauseVideo();
          videoButton.setAttribute("aria-pressed", "false");
          paused = true;
          setPrefersPaused(true);
        }
        videoButton.addEventListener("click", () => {
          if (paused == true) {
            playVideo();
          } else {
            pauseVideo();
          }
        });
      } // YouTube

      if (provider == "local") {
        const player = document.querySelector(".homepage-header__video video");

        let paused = false;

        if (prefersReducedMotion.matches) {
          // If user's OS is set to reduce motion
          // or if prefersPaused local storage is set to true
          // do not autoplay the video
          videoButton.setAttribute("aria-pressed", "false");
          videoButton.addEventListener("click", playVideo);
          pauseVideo();
        } else {
          // Start the video when ready
          heroOverlay.dataset.videoStarted = "true";
          videoButton.setAttribute("aria-pressed", "true");
          playVideo();
        }

        function playVideo() {
          player.play();
          videoButton.setAttribute("aria-pressed", "true");
          paused = false;
          heroOverlay.dataset.videoStarted = "true";
          setPrefersPaused(false);
        }
        function pauseVideo() {
          player.pause();
          videoButton.setAttribute("aria-pressed", "false");
          paused = true;
          setPrefersPaused(true);
        }
        player.onplaying = function () {
          videoButton.removeEventListener("click", playVideo);
          videoButton.addEventListener("click", pauseVideo);
        };
        player.onpause = function () {
          videoButton.removeEventListener("click", pauseVideo);
          videoButton.addEventListener("click", playVideo);
        };
      } // local
    }; // setUpVideo
    if (provider == "vimeo") {
      if (window.Vimeo) {
        setUpVideo();
      } else {
        const script = document.createElement("script");
        script.src = "//player.vimeo.com/api/player.js";
        script.onload = setUpVideo;
        document.head.appendChild(script);
      }
    }

    if (provider == "youtube") {
      window.onYouTubeIframeAPIReady = () => {
        setUpVideo();
      };
    }

    if (provider == "local") {
      const player = document.querySelector(".homepage-header__video video");
      player.addEventListener("loadeddata", () => {
        if (player.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          setUpVideo();
        }
      });
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setUpHomepageVideo);
  } else {
    setUpHomepageVideo();
  }
})();
